import { memo, useCallback, type ComponentProps, type MouseEvent } from 'react'
import { atom, useAtom } from 'jotai'
import { ArrowUpRight, Mic, Plus } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { FadingScrollable } from '@renderer/chat'
import { useCalendarEventsOnDay, useTodaysCalendarEvents } from '../../../hooks/use-upcoming-meetings'
import { useCalendars } from '@renderer/hooks/use-calendars'
import type { Person } from '@renderer/chat/hooks/usePeople'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useViewActions } from '../../../view-atoms'
import { useSidepanelUpcomingShowsEnded } from '../../../atoms'
import { MeetingPopoverRow } from '../../meeting/meeting-popover-row'
import { RecordingMeetingRow } from '../../meeting/recording-meeting-row'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import { noteNoun } from '@shared/utils/audio-notes/AudioNoteUtils'
import {
  useActiveAudioNoteTitle,
  useAudioNoteHasMeeting,
  useHasLiveAudioNote,
  useIsRecordingAudioNote,
} from '@renderer/hooks/audio/use-audio-note-selectors'
import { useCalendarEventForCurrentMeeting } from '@renderer/hooks/audio/use-recording-calendar-event'
import { useNewMeetingContext } from '../../meeting/use-new-meeting-context'
import { useLiveMeetingIdentity } from '../../meeting/use-live-meeting-identity'
import { MEETING_VIEW_ID } from '../../meeting/meeting-view-id'
import { SIDEPANEL_CELL_SURFACE } from '../../cells/surface'
import { listDayMeetings } from './select-upcoming-meetings'
import { nameUpcomingDay, UpcomingOptions } from './upcoming-options'
import { useHomeSectionLayout } from '../use-home-section-layout'
import { AUDIO_INDICATOR_COLOR } from '@renderer/hooks/audio/use-audio-indicator-tone'
import { HomeHeading, HomeHeadingCollapse } from '../nested/home-heading'
import { HomeViewMore } from '../nested/home-view-more'

/** Header gap the Upcoming scroller borrows above its first row so that row's shadow isn't clipped. */
const SHADOW_BLEED_PX = 6

// Outlives the section, so the meetings shown hold when Home remounts after a View closes.
const upcomingLimitAtom = atom<number | null>(null)
// Days from today, so it stays today across midnight. Not remembered past the session.
const upcomingDayOffsetAtom = atom(0)

type MeetingRowAppearance = ComponentProps<typeof MeetingPopoverRow>['appearance']

/**
 * Persistent quick-action row above the meetings (per the Day 0 design's
 * Section CTA): starts a meeting note and drills into the Meeting View. While a
 * note is live it becomes the way back into it. With no calendars connected
 * this row is the section body.
 */
const StartMeetingNoteRow = memo(function StartMeetingNoteRow({ appearance }: { appearance?: MeetingRowAppearance }) {
  const isTodo = appearance === 'todo'
  // Narrow selectors, matching SidepanelUpcomingList below. `hasLiveNote`
  // includes `saving`, so a save in flight can't be raced by starting a second recording.
  const hasActiveNote = useHasLiveAudioNote()
  const hasMeeting = useAudioNoteHasMeeting()
  const { openView } = useViewActions()
  const { startAudioNote } = useStartAudioNote()
  const newMeeting = useNewMeetingContext()
  const liveMeeting = useLiveMeetingIdentity()

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (hasActiveNote) {
        AmplitudeRenderer.trackEvent('Audio Note Action', {
          action: 'open active meeting',
          source: 'sidepanel upcoming',
        })
        openView(MEETING_VIEW_ID, liveMeeting, { sourceLabel: 'Home', pointerEvent: event })
        return
      }
      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'start taking notes',
        source: 'sidepanel upcoming',
      })
      void startAudioNote({
        source: 'assistant',
        onSuccessfulStart: () => {
          openView(MEETING_VIEW_ID, newMeeting, { sourceLabel: 'Home', pointerEvent: event })
        },
      })
    },
    [hasActiveNote, liveMeeting, newMeeting, openView, startAudioNote],
  )

  return (
    <button
      type="button"
      onClick={handleClick}
      // The shared cell surface supplies the hover container, and the
      // bg-transparent that keeps a bare <button> off Chromium's UA `buttonface` grey.
      className={twMerge(
        'group flex w-full items-center gap-2 px-3 text-left',
        isTodo ? 'py-1.5' : 'py-2',
        SIDEPANEL_CELL_SURFACE,
      )}
    >
      {/* Quiet until the row is hovered (Figma `6972:11353`). While a note is
          live the icon takes the recording green, so the row reads as a recording
          in progress rather than an idle CTA. */}
      <span
        className={twMerge(
          'flex size-5 shrink-0 items-center justify-center',
          hasActiveNote ? AUDIO_INDICATOR_COLOR.live : 'text-foreground-secondary group-hover:text-foreground',
        )}
      >
        <Mic size={14} />
      </span>
      <span
        className={twMerge(
          'text-foreground-secondary group-hover:text-foreground type-ui-sm min-w-0 flex-1 font-normal',
          isTodo && 'text-md',
        )}
      >
        {hasActiveNote ? `View ${noteNoun(hasMeeting)}` : 'Manually record a meeting'}
      </span>
      <span
        aria-hidden="true"
        className="text-foreground-secondary flex size-5 shrink-0 items-center justify-center opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
      >
        {hasActiveNote ? <ArrowUpRight size={14} /> : <Plus size={14} />}
      </span>
    </button>
  )
})

/** A live note that isn't one of the listed meetings, such as one started by hand. */
const RecordingAudioNoteRow = memo(function RecordingAudioNoteRow() {
  const activeTitle = useActiveAudioNoteTitle()
  const hasMeeting = useAudioNoteHasMeeting()
  const title = activeTitle?.trim() || (hasMeeting ? 'Meeting note' : 'Audio note')

  return <RecordingMeetingRow label={title} sourceLabel="Home" />
})

/**
 * Home's Upcoming as flat rows, each opening its meeting's popover. The meeting
 * being recorded becomes the live recording row, and the next one counts down to its start.
 */
export const SidepanelUpcomingList = memo(function SidepanelUpcomingList({
  heading,
  paging,
  rowAppearance,
  peopleByEmail,
}: {
  /** Drawn above the rows, with a count of the meetings and the options for which day to show. */
  heading?: string
  /** Meetings shown before the first "View more", and how many each adds. Unset, every meeting shows. */
  paging?: { first: number; more: number }
  rowAppearance?: MeetingRowAppearance
  /** Passed in rather than fetched here, so only the layouts that name attendees load the directory. */
  peopleByEmail?: ReadonlyMap<string, Person>
}) {
  const { data: todaysEvents } = useTodaysCalendarEvents()
  // Only the headed layout has the options, so any other always shows what's left of today.
  const [storedDayOffset, setDayOffset] = useAtom(upcomingDayOffsetAtom)
  const [storedShowsEnded, setShowsEnded] = useSidepanelUpcomingShowsEnded()
  const dayOffset = heading ? storedDayOffset : 0
  const showsEnded = heading ? storedShowsEnded : false
  const { data: daysEvents } = useCalendarEventsOnDay(dayOffset)
  const { googleAccounts, outlookAccounts, calendars } = useCalendars()
  const { upcomingMaxHeight } = useHomeSectionLayout()
  const [shownLimit, setShownLimit] = useAtom(upcomingLimitAtom)
  // Narrow audio-note-state slices, so status ticks during a recording don't re-render the section.
  const hasLiveNote = useHasLiveAudioNote()
  const isRecording = useIsRecordingAudioNote()
  // Resolved through the shared link predicate rather than the stored
  // calendarEventId, which can still be empty while a meeting is being detected (HIG-5856).
  const activeMeetingEvent = useCalendarEventForCurrentMeeting(todaysEvents)
  // Gated on hasLiveNote so a paused note keeps its live row.
  const liveEventId = hasLiveNote ? (activeMeetingEvent?.id ?? null) : null
  // Includes paused, so a paused recording still holds its meeting in the list
  // once it overruns the scheduled end.
  const recordingEventId = isRecording ? (activeMeetingEvent?.id ?? null) : null

  const hasCalendars = googleAccounts.length + outlookAccounts.length > 0
  const meetings = hasCalendars
    ? listDayMeetings(daysEvents, {
        isToday: dayOffset === 0,
        showsEnded,
        activeTranscriptEventId: liveEventId,
        now: new Date(),
        recordingEventId,
      })
    : []
  const limit = paging ? (shownLimit ?? paging.first) : meetings.length
  const isExpanded = paging !== undefined && limit > paging.first
  const visibleMeetings = meetings.slice(0, limit)
  const showsLiveMeetingRow = visibleMeetings.some((meeting) => meeting.id === liveEventId)
  // The meeting being recorded already reads as live, so the countdown moves to the one after it.
  const countdownMeetingId = visibleMeetings.find((meeting) => meeting.id !== liveEventId)?.id

  // The negative margins and matching padding give the rows' shadows room inside
  // the scroller's clip, the same bleed the collapsible fold takes. SHADOW_BLEED_PX
  // borrows the fold's header gap above, and the cap takes it back.
  return (
    <>
      {heading && (
        <HomeHeading
          count={meetings.length > 0 ? String(meetings.length) : undefined}
          action={isExpanded && <HomeHeadingCollapse section={heading} onClick={() => setShownLimit(null)} />}
          options={
            <UpcomingOptions
              dayOffset={dayOffset}
              onDayOffsetChange={(next) => {
                setDayOffset(next)
                setShownLimit(null)
              }}
              showsEnded={showsEnded}
              onShowsEndedChange={setShowsEnded}
            />
          }
        >
          {dayOffset === 0 ? heading : nameUpcomingDay(dayOffset)}
        </HomeHeading>
      )}
      <FadingScrollable
        // The To-dos Home is inset 8px, so its bleed stops at the panel's edge.
        className={rowAppearance === 'todo' ? '-mx-2 -mb-3 px-2 pb-3' : '-mx-3 -mb-3 px-3 pb-3'}
        style={{
          marginTop: -SHADOW_BLEED_PX,
          paddingTop: SHADOW_BLEED_PX,
          maxHeight: upcomingMaxHeight + SHADOW_BLEED_PX,
        }}
        fadeSize="12%"
      >
        <div className="flex flex-col gap-0.5">
          {/* The live meeting row already opens the note, so no View row above it. */}
          {hasLiveNote ? (
            !showsLiveMeetingRow && <RecordingAudioNoteRow />
          ) : (
            <StartMeetingNoteRow appearance={rowAppearance} />
          )}
          {/* No `content-visibility: auto` on these rows: base-ui measures the
              collapsible panel once as it opens, and a skipped row would lay out
              at its placeholder height. */}
          {visibleMeetings.map((meeting) => (
            <MeetingPopoverRow
              key={meeting.id}
              meeting={meeting}
              calendars={calendars}
              isRecording={meeting.id === liveEventId}
              appearance={rowAppearance}
              showCountdown={meeting.id === countdownMeetingId}
              peopleByEmail={peopleByEmail}
            />
          ))}
          {paging && meetings.length > limit && <HomeViewMore onClick={() => setShownLimit(limit + paging.more)} />}
        </div>
      </FadingScrollable>
    </>
  )
})
